import React, { useState, useRef, useEffect, useCallback } from "react";
import ".././popup.common.css";
import styles from "./Style.module.css";
import BorderedButton from "../../Buttons/BorderedButton";
import PrimaryButton from "../../Buttons/PrimaryButton";
import { BtnLink } from "../../Buttons/BtnLink";
import { postMembershipPassword, getCustomerList } from "../../../APICalling/APICalling";
import {
	buildCustomerListRequest,
	buildMembershipCustomerListExtraParams,
	extractMembershipIdFromListResponse,
	resolveMembershipIdForApi,
} from "../../../Utils/customerSearchUtils";
import {
	getMembershipPinInputProps,
	MembershipPinAutofillTrap,
} from "./membershipPinInput";

const PIN_LENGTH = 4;

const parseVerifyResponse = (res) => {
	if (!res || typeof res !== "object") {
		return { authorized: false, message: "Unexpected response. Please try again." };
	}
	const nested = res.data && typeof res.data === "object";
	const useNestedAsLayer =
		nested && ("success" in res.data || "message" in res.data || "errors" in res.data);
	const layer = useNestedAsLayer ? res.data : res;
	const success = layer.success === true || res.success === true;
	const innerPayload = layer.data && typeof layer.data === "object" ? layer.data : null;
	const inner =
		innerPayload && typeof innerPayload.is_authorized === "boolean"
			? innerPayload
			: typeof layer.is_authorized === "boolean"
				? layer
				: null;
	const authorized = Boolean(inner?.is_authorized);
	const message =
		(typeof layer.message === "string" && layer.message.trim()) ||
		(typeof res.message === "string" && res.message.trim()) ||
		"";
	if (success && authorized) {
		return { authorized: true, message: message || "Membership verified successfully." };
	}
	if (message) {
		return { authorized: false, message };
	}
	return {
		authorized: false,
		message: success && !authorized ? "Incorrect PIN." : "Unable to verify membership.",
	};
};

const PasswordForMembership = ({
	onClose,
	onSkip,
	onSubmitted,
	membershipId,
	membershipUserId = null,
	onShowSkipStep,
	onShowResetStep,
	hideResetPassword = false,
}) => {
	const [digits, setDigits] = useState(() => Array(PIN_LENGTH).fill(""));
	const [submitError, setSubmitError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const inputRefs = useRef([]);
	const dismissedRef = useRef(false);

	useEffect(() => {
		const t = requestAnimationFrame(() => {
			inputRefs.current[0]?.focus();
		});
		return () => cancelAnimationFrame(t);
	}, []);

	const handleChange = useCallback((index, value) => {
		setSubmitError((prev) => (prev ? "" : prev));
		const nextChar = (value || "").replace(/\D/g, "").slice(-1);
		setDigits((prev) => {
			const next = [...prev];
			next[index] = nextChar;
			return next;
		});
		if (nextChar && index < PIN_LENGTH - 1) {
			inputRefs.current[index + 1]?.focus();
		}
	}, []);

	const submitRef = useRef(null);

	const handleKeyDown = useCallback((index, event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			submitRef.current?.();
			return;
		}
		if (event.key !== "Backspace" || index === 0) return;
		if (event.target.value === "") {
			event.preventDefault();
			inputRefs.current[index - 1]?.focus();
		}
	}, []);

	const handlePaste = useCallback((event) => {
		event.preventDefault();
		setSubmitError((prev) => (prev ? "" : prev));
		const raw = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, PIN_LENGTH);
		if (!raw) return;
		setDigits(() => {
			const next = Array(PIN_LENGTH).fill("");
			for (let i = 0; i < raw.length; i += 1) {
				next[i] = raw[i];
			}
			return next;
		});
		requestAnimationFrame(() => {
			const focusAt = Math.min(raw.length, PIN_LENGTH - 1);
			inputRefs.current[focusAt]?.focus();
		});
	}, []);

	const pinComplete = digits.every((d) => d !== "");

	const resolveMembershipIdForRequest = async () => {
		const fromProp = resolveMembershipIdForApi({ membership_id: membershipId });
		if (fromProp != null) return fromProp;

		if (membershipUserId == null || membershipUserId === "") {
			return null;
		}

		const { item } = buildCustomerListRequest(
			"",
			null,
			buildMembershipCustomerListExtraParams(membershipUserId)
		);
		const response = await getCustomerList(item);
		return extractMembershipIdFromListResponse(response);
	};

	const handleSubmit = async () => {
		if (!pinComplete || isSubmitting) return;
		const pin = digits.join("");
		setSubmitError("");
		setIsSubmitting(true);
		try {
			const membershipIdValue = await resolveMembershipIdForRequest();
			if (dismissedRef.current) return;
			if (membershipIdValue == null) {
				setSubmitError("Membership is missing. Close and try again.");
				return;
			}
			const res = await postMembershipPassword({
				membership_id: membershipIdValue,
				pin,
			});
			if (dismissedRef.current) return;
			const { authorized, message } = parseVerifyResponse(res);
			if (authorized && typeof onSubmitted === "function") {
				onSubmitted(pin);
				return;
			}
			setSubmitError(message || "Verification failed.");
		} catch {
			if (dismissedRef.current) return;
			setSubmitError("Network error. Please try again.");
		} finally {
			if (!dismissedRef.current) {
				setIsSubmitting(false);
			}
		}
	};
	submitRef.current = handleSubmit;

	const handleDismiss = () => {
		dismissedRef.current = true;
		onClose?.();
	};

	const handleSkip = () => {
		if (typeof onShowSkipStep === "function") {
			onShowSkipStep();
			return;
		}
		if (typeof onSkip === "function") {
			onSkip(true);
		}
		onClose();
	};

	return (
		<div className={`${styles.passwordForMembership} popup`}>
			<button
				type="button"
				className={styles.closeButton}
				onClick={handleDismiss}
				aria-label="Close"
			>
				&times;
			</button>
			<div className="header">
				<h2>Enter Password</h2>
				<p>To utilize credits for this transaction.</p>
			</div>

			<div className="body">
				<MembershipPinAutofillTrap />
				<div className={styles.pinRow} onPaste={handlePaste}>
					{digits.map((value, index) => (
						<input
							key={`membership-pin-${index}`}
							ref={(el) => {
								inputRefs.current[index] = el;
							}}
							{...getMembershipPinInputProps("enter", index)}
							maxLength={1}
							aria-label={`Digit ${index + 1} of ${PIN_LENGTH}`}
							className={`form-control ${styles.pinBox}`}
							value={value}
							onChange={(e) => handleChange(index, e.target.value)}
							onKeyDown={(e) => handleKeyDown(index, e)}
						/>
					))}
				</div>
				<div className={styles.membershipPinFormError} role="alert" aria-live="polite">
					{submitError || null}
				</div>
			</div>

			<div className="footer">
				<BorderedButton text="Skip Password" onClick={handleSkip} />
				<PrimaryButton
					text={isSubmitting ? "Verifying..." : "Submit"}
					onClick={handleSubmit}
					disabled={!pinComplete || isSubmitting}
				/>
			</div>
			{!hideResetPassword && (
				<div className={`reset-password ${styles.resetPassword}`}>
					<BtnLink
						text="Reset Password"
						className={styles.resetPasswordLink}
						handleClick={onShowResetStep}
					/>
				</div>
			)}
		</div>
	);
};

export default PasswordForMembership;
